import { useForm } from "react-hook-form";
import { ColumnDef } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import dayjs from "dayjs";
import {
  CheckCircle,
  XCircle,
  ClipboardList,
  Plus,
  CheckCheck
} from "lucide-react";

import {
  useDeleteData,
  useGetData,
  usePostData,
  usePutData
} from "@/hooks/services/use-query-hooks";
import {
  AddJobOrderDynamic,
  AddJobOrderDynamicAttribute,
  CompleteJobOrder,
  DeleteJobOrderDynamicAttribute,
  JobOrdersDynamic,
  JobOrdersDynamicAttribute,
  JobOrdersDynamicAttributeDataType,
  UpdateJobOrderDynamicAttribute,
  UpdateJobOrdersDynamic
} from "@/hooks/services/pps/handlers/job-orders-dynamic-api";
import {
  AddJobOrderWorkflow,
  JobOrderWorkflow
} from "@/hooks/services/pps/handlers/job-order-workflow";

import {
  SearchFilterBar,
  useFilterBuilder,
  useSortBuilder,
  AttributeDataType,
  serializeFiltersToLegacyFormat,
  serializeSortsToLegacyFormat,
  type FilterableAttribute,
  type SortableAttribute
} from "@/components/search-filter-bar";

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/datatable/data-table";

import { AddAttributeSheet } from "../components/add-attribute-sheet";
import DynamicSheetContent, {
  type JobOrderDrawerInitialTab,
  type JobOrderWorkflowMode
} from "../components/drawer/dynamic-drawer-content";
import { AddJobOrderSheet } from "../components/add-job-order-sheet";
import { EditHeaderActionSheet } from "../components/edit-header-action-sheet";

import { capitalize } from "@/lib/utils";
import { isDateTimeWithClockField } from "@/lib/pps-date-time";
import { toastManager } from "@/lib/toast-manager";

import { useWorkSpaceStore } from "@/stores/store";
import { EMPTY_PROCESS_LABEL, getNestedValue, renderCell } from "./utils";
import { JobOrderAttributeDataType } from "./constant";
import { ColumnHeaderActions } from "../components/column-header-actions";
import { WorkspaceToggle } from "@/components/workspace-toggle";
import { useDebounce } from "@/hooks/use-debounce";
import { useJobOrderRankUpdate } from "@/hooks/use-job-order-rank-update";
import {
  REFERENCE_TABLE,
  useForeignKeyData
} from "@/hooks/use-foreign-key-data";

import { usePermissions } from "@/hooks/use-permissions";
import { DeleteHeaderActionDialog } from "../components/delete-header-action-dialog";

// Map JobOrderAttributeDataType to AttributeDataType for SearchFilterBar
function mapToAttributeDataType(dataType: number): AttributeDataType {
  switch (dataType) {
    case JobOrderAttributeDataType.FOREIGN_KEY:
      return AttributeDataType.FOREIGN_KEY;
    case JobOrderAttributeDataType.TEXT:
      return AttributeDataType.TEXT;
    case JobOrderAttributeDataType.INT:
      return AttributeDataType.INT;
    case JobOrderAttributeDataType.FRACTION:
      return AttributeDataType.FRACTION;
    case JobOrderAttributeDataType.DATETIME:
      return AttributeDataType.DATETIME;
    case JobOrderAttributeDataType.BOOLEAN:
      return AttributeDataType.BOOLEAN;
    default:
      return AttributeDataType.TEXT;
  }
}

export const Route = createFileRoute("/plant-3/list/")({
  component: RouteComponent
});

// eslint-disable-next-line react-refresh/only-export-components
export const renderBooleanIcon = (value: boolean) => {
  return value ? (
    <CheckCircle className="aero:w-5 aero:h-5 aero:!text-green-500" />
  ) : (
    <XCircle className="aero:w-5 aero:h-5 aero:!text-red-500" />
  );
};

type SelectOption = { label: string; value: string | number };

function RouteComponent() {
  const form = useForm({
    defaultValues: {
      label: "",
      dataType: "",
      defaultValue: "",
      isUnique: false,
      isRequired: false,
      isActive: true
    }
  });

  const { workspace } = useWorkSpaceStore();
  // WOR-1793: Permission hook reserved for future use
  const permissions = usePermissions();

  // Drag-and-drop rank update hook
  const { handleReorder } = useJobOrderRankUpdate({
    workspaceId: workspace?.id || 0,
    onSuccess: () => {
      // Refetch job orders to reflect new rank order
      if (refetch) refetch();
      // Clear row selection after successful reorder
      if (tableInstance) {
        tableInstance.resetRowSelection();
      }
    }
  });

  // WOR-1793 Req #8: Show unauthorized message if no Plant3_ViewJob permission
  if (!permissions.canViewPlant3()) {
    return (
      <div className="aero:flex aero:items-center aero:justify-center aero:h-[calc(100vh-200px)]">
        <div className="aero:text-center aero:space-y-4">
          <h2 className="aero:!text-2xl aero:!font-semibold aero:!text-foreground">
            You do not have permission to view this page
          </h2>
        </div>
      </div>
    );
  }

  const filterParams = {
    ...(workspace?.id && { workspaceId: workspace?.id })
  };

  const [selectedItem, setSelectedItem] = useState<{
    show: boolean;
    id: number | null;
    initialTab: JobOrderDrawerInitialTab;
    workflowMode: JobOrderWorkflowMode;
  }>({
    show: false,
    id: null,
    initialTab: "job-information",
    workflowMode: "view"
  });

  const openJobOrderDetail = (
    id: number,
    options?: {
      initialTab?: JobOrderDrawerInitialTab;
      workflowMode?: JobOrderWorkflowMode;
    }
  ) => {
    setSelectedItem({
      id,
      show: true,
      initialTab: options?.initialTab ?? "job-information",
      workflowMode: options?.workflowMode ?? "view"
    });
  };

  const [showEditDialog, setShowEditDialog] = useState(false);
  const [refetch, setRefetch] = useState<() => void>(() => {});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Use useForeignKeyData for all foreign key types
  const { optionsData: customerEditOptions, setSearch: setCustomerEditSearch } =
    useForeignKeyData(REFERENCE_TABLE.ppsCustomer, "customerName");

  const { optionsData: partEditOptions, setSearch: setPartEditSearch } =
    useForeignKeyData(REFERENCE_TABLE.ppsPart, [
      "partNumber",
      "partDescription"
    ]);

  const { optionsData: processEditOptions, setSearch: setProcessEditSearch } =
    useForeignKeyData(REFERENCE_TABLE.ppsProcess, "processName");

  const { optionsData: machineEditOptions, setSearch: setMachineEditSearch } =
    useForeignKeyData(REFERENCE_TABLE.ppsMachine, ["machineName", "name"]);

  const { optionsData: technicianOptions, setSearch: setTechnicianEditSearch } =
    useForeignKeyData(REFERENCE_TABLE.ppsTechnician, [
      "technicianName",
      "name"
    ]);

  const workflowProcessOptionsCacheRef = useRef<Record<number, SelectOption[]>>(
    {}
  );

  // Clear the workflow options cache whenever processEditOptions loads/changes so
  // cached fallback labels (built before the list was ready) get replaced with
  // the proper enriched option labels on the next cell open.
  useEffect(() => {
    workflowProcessOptionsCacheRef.current = {};
  }, [processEditOptions]);

  const customerFilterOptions = customerEditOptions;
  const partFilterOptions = partEditOptions;
  const processFilterOptions = processEditOptions;
  const machineFilterOptions = machineEditOptions;
  const technicianFilterOptions = technicianOptions;

  const [showAttributeDialog, setShowAttributeDialog] = useState(false);
  const [showJobOrderSheet, setShowJobOrderSheet] = useState(false);
  const [selectedAttribute, setSelectedAttribute] = useState<any>(null);
  const [tableInstance, setTableInstance] = useState<any>(null);

  // Search state
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);

  // Filter and Sort state using new hooks
  const { filters, addFilter, updateFilter, removeFilter, clearFilters } =
    useFilterBuilder();
  const { sorts, addSort, updateSort, removeSort, clearSorts } =
    useSortBuilder();

  const debouncedFilters = useDebounce(filters, 500);
  const debouncedSorts = useDebounce(sorts, 500);

  const jobOrderDynamicDataType = useGetData(
    JobOrdersDynamicAttributeDataType()
  );
  const addJobOrderDynamic = usePostData(AddJobOrderDynamic());
  const dynamicJobAttribute = useGetData(
    JobOrdersDynamicAttribute({
      ...filterParams,
      isActive: true
    })
  );

  const addAttribute = usePostData(AddJobOrderDynamicAttribute());
  const updateAttribute = usePutData(UpdateJobOrderDynamicAttribute());
  const deleteAttribute = useDeleteData(DeleteJobOrderDynamicAttribute());
  const updateJobOrdersDynamic = usePutData(UpdateJobOrdersDynamic());
  const completeJobOrder = usePutData(CompleteJobOrder());
  const addJobOrderWorkflow = usePostData(AddJobOrderWorkflow());

  const handleRefetch = (callback: () => void) => {
    setRefetch(() => callback);
  };

  const systemAttributeIds = useMemo(
    () =>
      new Set<string>(
        (dynamicJobAttribute?.data?.data ?? [])
          .filter((a: any) => a.isSystemAttribute)
          .map((a: any) => String(a.id))
      ),
    [dynamicJobAttribute?.data?.data]
  );

  const columns = useMemo<ColumnDef<any>[]>(() => {
    if (!dynamicJobAttribute?.data?.data) return [];

    return dynamicJobAttribute.data.data
      .sort((a: any, b: any) => a.id - b.id)
      .map((item: any) => {
        return {
          accessorFn: (row: any) => getNestedValue(row, item),
          header: capitalize(item.label),
          cell: (cellInfo: any) => {
            const value = getNestedValue(cellInfo.row.original, item);

            if (item.name === "process" && value === EMPTY_PROCESS_LABEL) {
              return (
                <span className="aero:italic aero:text-[#ff6900] aero:text-sm aero:leading-5">
                  {EMPTY_PROCESS_LABEL}
                </span>
              );
            }

            switch (item.dataType) {
              case JobOrderAttributeDataType.BOOLEAN: {
                return renderBooleanIcon(Boolean(value));
              }
              case JobOrderAttributeDataType.DATETIME: {
                const value = getNestedValue(cellInfo.row.original, item);
                const format = isDateTimeWithClockField(item.name, item.label)
                  ? "DD-MMM-YYYY HH:mm"
                  : "DD-MMM-YYYY";

                return value ? dayjs(value).format(format) : "";
              }

              default:
                return renderCell(cellInfo, item);
            }
          },
          id: `${item.id}-${item.index}`,
          type: item.dataType,
          attribute: item.name,
          nestedColumnName: item.nestedColumnName ?? "",
          referenceTable: item.referenceTable,
          editable: true
        };
      });
  }, [dynamicJobAttribute?.data?.data, getNestedValue, renderCell]);

  const normalizeProcessLabel = (value: string) =>
    value
      .toLowerCase()
      .replace(/^\s*\d+\s*-\s*/, "")
      .replace(/\s+/g, " ")
      .trim();

  const filterSelectOptions = (options: SelectOption[], keyword: string) => {
    const normalizedKeyword = keyword.trim().toLowerCase();
    if (!normalizedKeyword) {
      return options;
    }

    return options.filter((option) =>
      option.label.toLowerCase().includes(normalizedKeyword)
    );
  };

  const mapWorkflowProcessOptions = useCallback(
    (workflows: any[]): SelectOption[] => {
      if (!Array.isArray(workflows) || workflows.length === 0) {
        return [];
      }

      const optionsByProcessId = new Map<number, SelectOption>();

      workflows.forEach((workflow: any) => {
        const processId = Number(workflow?.processId);
        if (!Number.isFinite(processId) || processId <= 0) {
          return;
        }

        const matchedOption = processEditOptions.find(
          (option: SelectOption) => Number(option.value) === processId
        );

        // Fall back to the processName from the workflow response when the
        // options list hasn't loaded yet or doesn't contain this process.
        const optionToUse: SelectOption = matchedOption ?? {
          value: processId,
          label: String(workflow?.processName || `Process ${processId}`)
        };

        optionsByProcessId.set(processId, optionToUse);
      });

      if (optionsByProcessId.size > 0) {
        const orderedOptions: SelectOption[] = [];
        const seenProcessIds = new Set<number>();

        workflows.forEach((workflow: any) => {
          const processId = Number(workflow?.processId);
          if (
            !Number.isFinite(processId) ||
            processId <= 0 ||
            seenProcessIds.has(processId)
          ) {
            return;
          }

          const option = optionsByProcessId.get(processId);
          if (option) {
            orderedOptions.push(option);
            seenProcessIds.add(processId);
          }
        });

        return orderedOptions;
      }

      const normalizedWorkflowNames = new Set(
        workflows
          .map((workflow: any) =>
            normalizeProcessLabel(workflow?.processName || "")
          )
          .filter(Boolean)
      );

      return processEditOptions.filter((option: SelectOption) =>
        normalizedWorkflowNames.has(normalizeProcessLabel(option.label))
      );
    },
    [processEditOptions]
  );

  const getWorkflowProcessOptions = useCallback(
    async (
      jobOrderId: number,
      forceRefresh = false
    ): Promise<SelectOption[]> => {
      const cachedOptions = workflowProcessOptionsCacheRef.current[jobOrderId];

      if (!forceRefresh && cachedOptions) {
        return cachedOptions;
      }

      const response = await JobOrderWorkflow(jobOrderId).handle();
      const workflows = Array.isArray(response?.data?.data)
        ? response.data.data
        : Array.isArray(response?.data)
          ? response.data
          : [];

      const mappedOptions = mapWorkflowProcessOptions(workflows);
      workflowProcessOptionsCacheRef.current[jobOrderId] = mappedOptions;

      return mappedOptions;
    },
    [mapWorkflowProcessOptions]
  );

  const handleForeignKeySearch = (
    referenceTable: string,
    search: string,
    callback: (options: SelectOption[]) => void,
    row?: { original?: { id?: number } }
  ) => {
    // Normalize to lowercase for comparison
    const normalizedRef = referenceTable.toLowerCase();

    if (normalizedRef === REFERENCE_TABLE.ppsCustomer.toLowerCase()) {
      setCustomerEditSearch(search);
      callback(customerEditOptions);
    } else if (normalizedRef === REFERENCE_TABLE.ppsPart.toLowerCase()) {
      setPartEditSearch(search);
      callback(partEditOptions);
    } else if (normalizedRef === REFERENCE_TABLE.ppsProcess.toLowerCase()) {
      const jobOrderId = Number(row?.original?.id);

      if (Number.isFinite(jobOrderId) && jobOrderId > 0) {
        const shouldRefresh = search.trim().length === 0;

        void getWorkflowProcessOptions(jobOrderId, shouldRefresh)
          .then((options) => {
            callback(filterSelectOptions(options, search));
          })
          .catch(() => {
            callback([]);
          });

        return;
      }

      setProcessEditSearch(search);
      callback(filterSelectOptions(processEditOptions, search));
    } else if (normalizedRef === REFERENCE_TABLE.ppsMachine.toLowerCase()) {
      setMachineEditSearch(search);
      callback(machineEditOptions);
    } else if (normalizedRef === REFERENCE_TABLE.ppsTechnician.toLowerCase()) {
      setTechnicianEditSearch(search);
      callback(technicianOptions);
    } else {
      callback([]);
    }
  };

  const handleEditValue = (value: any, id: number, setEditingCell: any) => {
    const body = {
      ...value,
      workspaceId: workspace?.id || null
    };

    updateJobOrdersDynamic.mutate(
      { body, id },
      {
        onSuccess: () => {
          toastManager.showSuccess(
            "Job Order Updated",
            "Job Order has been successfully updated."
          );
          setEditingCell(null);
          if (refetch) refetch();
        },
        onError: (error) => {
          toastManager.showError(
            "Update Failed",
            error?.message || "Failed to update the job order"
          );
        }
      }
    );
  };

  // Handler for drag-and-drop row reordering (supports batch updates)
  const handleRowReorder = useCallback(
    async (
      oldIndex: number,
      newIndex: number,
      _pagination: any,
      data: any[],
      selectedIds?: (string | number)[]
    ) => {
      if (!workspace?.id) {
        toastManager.showError("Workspace not found");
        return;
      }

      if (!data || data.length === 0) {
        toastManager.showError("No data available for reordering");
        return;
      }

      // Convert selected IDs to numbers if provided
      const selectedJobOrderIds = selectedIds?.map((id) => Number(id));

      await handleReorder(data, oldIndex, newIndex, selectedJobOrderIds);
    },
    [handleReorder, workspace?.id]
  );

  const handleSubmitNewAttribute = (data: any, attributeForm: any) => {
    const body = {
      defaultValue: data.defaultValue,
      isActive: data.isActive,
      isRequired: data.isRequired,
      isUnique: data.isUnique,
      dataType: Number(data.dataType),
      label: data.label,
      workspaceId: workspace?.id
    };

    addAttribute.mutate(body, {
      onSuccess: () => {
        toastManager.showSuccess(
          "Attribute Created",
          "New attribute has been successfully created."
        );
        attributeForm.reset();
        dynamicJobAttribute.refetch();
        if (refetch) refetch();
        setShowAttributeDialog(!showAttributeDialog);
      },
      onError: (error: any) => {
        toastManager.showError(
          "Creation Failed",
          error?.message || "Failed to create the attribute"
        );
      }
    });
  };

  const handleUpdateAttribute = useCallback(
    (data: any) => {
      if (!selectedAttribute?.id) {
        toastManager.showError(
          "Update Failed",
          "No attribute selected for update"
        );
        return;
      }

      const body = {
        defaultValue: String(data.defaultValue || ""),
        isActive: data.isActive,
        isRequired: data.isRequired,
        isUnique: data.isUnique,
        dataType: Number(data.dataType),
        workspaceId: workspace?.id,
        columns: [
          {
            label: data.label,
            index: selectedAttribute.index || 0
          }
        ]
      };

      updateAttribute.mutate(
        { body, id: selectedAttribute.id },
        {
          onSuccess: () => {
            setShowEditDialog((prev) => !prev);
            toastManager.showSuccess(
              "Attribute Updated",
              "Attribute has been successfully updated."
            );

            dynamicJobAttribute.refetch();
            if (refetch) refetch();
          },
          onError: (error: any) => {
            toastManager.showError(
              "Update Failed",
              error?.message || "Failed to update the attribute"
            );
          }
        }
      );
    },
    [selectedAttribute, workspace?.id]
  );

  const handleAddJobOrderDynamic = (
    data: any,
    setOpen: (value: boolean) => void,
    resetForm: () => void,
    setSelectedProcesses: (value: any[]) => void,
    selectedProcesses: any[]
  ) => {
    const body = {
      ...data,
      workspaceId: workspace?.id
    };

    const finalizeCreate = () => {
      dynamicJobAttribute.refetch();
      if (refetch) refetch();
      resetForm();
      setOpen(false);
      setSelectedProcesses([]);
    };

    addJobOrderDynamic.mutate(body, {
      onSuccess: (response: any) => {
        const createdJobOrderId = Number(response?.data?.jobOrderId);
        const hasSelectedProcesses =
          Array.isArray(selectedProcesses) && selectedProcesses.length > 0;

        if (!hasSelectedProcesses || !createdJobOrderId) {
          toastManager.showSuccess(
            "Job Order Created",
            "New job order has been successfully created."
          );
          finalizeCreate();
          return;
        }

        const partProcesses = selectedProcesses.map((process: any) => ({
          partProcessId: process.partProcessId,
          processName: `${process.processNo} - ${process.processName}`,
          processStandardHours: process.processStandardHour ?? 0,
          labourStandardHours: 0
        }));

        addJobOrderWorkflow.mutate(
          {
            jobOrderId: createdJobOrderId,
            partProcesses
          },
          {
            onSuccess: () => {
              toastManager.showSuccess(
                "Job Order Created",
                "New job order and workflow have been successfully created."
              );
              finalizeCreate();
            },
            onError: (error: any) => {
              toastManager.showInfo(
                "Job Order Created With Pending Workflow",
                error?.message ||
                  "Job order is created, but workflow initialization failed. You can continue from Update Process."
              );
              finalizeCreate();
            }
          }
        );
      },
      onError: (error: any) => {
        toastManager.showError(
          "Creation Failed",
          error?.message || "Failed to create the job order"
        );
      }
    });
  };

  const onDeleteAttribute = (confirmLabel: string) => {
    if (selectedAttribute?.id) {
      deleteAttribute.mutate(
        { id: selectedAttribute.id, confirmLabel },
        {
          onSuccess: () => {
            setShowDeleteDialog((prev) => !prev);
            toastManager.showSuccess(
              "Attribute Deleted",
              "Attribute has been successfully deleted."
            );
            dynamicJobAttribute.refetch();
            if (refetch) refetch();
          },
          onError: (error: any) => {
            toastManager.showError(
              "Deletion Failed",
              error?.message || "Failed to delete the attribute"
            );
          }
        }
      );
    }
    setSelectedAttribute(null);
  };

  const handleCompleteJob = useCallback(
    (jobOrderId: number, isComplete: boolean = true) => {
      completeJobOrder.mutate(
        { id: jobOrderId, isComplete },
        {
          onSuccess: () => {
            toastManager.showSuccess(
              isComplete ? "Job Completed" : "Job Reopened",
              isComplete
                ? "Job order has been marked as complete."
                : "Job order has been reopened."
            );
            if (refetch) refetch();
          },
          onError: (error: any) => {
            toastManager.showError(
              "Action Failed",
              error?.message || "Failed to update job order status"
            );
          }
        }
      );
    },
    [completeJobOrder, refetch]
  );

  const formatAttributes = (attributes: any[]) => {
    if (!attributes) return [];

    const result: any[] = [];
    const seenIds = new Set<number>();
    const foreignKeyGroups = new Map<string, any[]>();

    attributes.forEach((item) => {
      // Group foreign key attributes by name (not referenceTable + name)
      // This ensures part.partNumber and part.partDescription are grouped together
      if (item.referenceTable && item.nestedColumnName) {
        const groupKey = item.name; // Just use 'part', 'customer', 'process'
        if (!foreignKeyGroups.has(groupKey)) {
          foreignKeyGroups.set(groupKey, []);
        }
        foreignKeyGroups.get(groupKey)!.push(item);
      } else {
        // Non-foreign-key attributes: add only once per ID
        if (!seenIds.has(item.id)) {
          seenIds.add(item.id);
          result.push({ ...item });
        }
      }
    });

    // Process foreign key groups - one entry per FK relationship
    foreignKeyGroups.forEach((items, groupKey) => {
      // Sort by index to maintain order from API
      items.sort((a, b) => (a.index || 0) - (b.index || 0));

      // Use the first item as the base
      const firstItem = items[0];

      // Collect all nested column names for this FK
      const allNestedFields = items.map((item) => item.nestedColumnName);

      // Use a friendly singular label (capitalize first letter)
      const friendlyLabel =
        groupKey.charAt(0).toUpperCase() + groupKey.slice(1);

      result.push({
        ...firstItem,
        // Override label to be singular and clean
        label: friendlyLabel,
        // Store all nested fields for search/display
        nestedColumnNames: allNestedFields,
        allNestedFields: allNestedFields,
        // Keep first nestedColumnName for backward compatibility
        nestedColumnName: firstItem.nestedColumnName
      });
    });

    // Sort: FK fields first, then others by ID
    return result.sort((a, b) => {
      // Prioritize FK fields
      if (a.referenceTable && !b.referenceTable) return -1;
      if (!a.referenceTable && b.referenceTable) return 1;
      // Then sort by id
      return a.id - b.id;
    });
  };

  // Memoized filterable attributes for SearchFilterBar
  const filterableAttributes = useMemo((): FilterableAttribute[] => {
    const formattedAttributes = formatAttributes(
      dynamicJobAttribute?.data?.data
    );
    if (!formattedAttributes || formattedAttributes.length === 0) return [];

    return formattedAttributes.map((attr: any) => {
      // Check if this is a foreign key field
      const isForeignKey = !!attr.referenceTable;

      // For FK fields, determine which options to use based on referenceTable
      let options: { label: string; value: string | number }[] | undefined;

      if (isForeignKey) {
        const normalizedRef = attr.referenceTable.toLowerCase();

        if (normalizedRef === REFERENCE_TABLE.ppsCustomer.toLowerCase()) {
          options = customerFilterOptions;
        } else if (normalizedRef === REFERENCE_TABLE.ppsPart.toLowerCase()) {
          options = partFilterOptions;
        } else if (normalizedRef === REFERENCE_TABLE.ppsProcess.toLowerCase()) {
          options = processFilterOptions;
        } else if (normalizedRef === REFERENCE_TABLE.ppsMachine.toLowerCase()) {
          options = machineFilterOptions;
        } else if (
          normalizedRef === REFERENCE_TABLE.ppsTechnician.toLowerCase()
        ) {
          options = technicianFilterOptions;
        }
      }

      return {
        id: String(attr.id),
        field: attr.name,
        label: attr.label || attr.name,
        dataType: isForeignKey
          ? AttributeDataType.FOREIGN_KEY
          : mapToAttributeDataType(attr.dataType),
        nestedField: attr.nestedColumnName,
        referenceTable: attr.referenceTable,
        options: options,
        foreignKeyLabelField: attr.nestedColumnName || "name",
        foreignKeyValueField: "id"
      };
    });
  }, [
    dynamicJobAttribute?.data?.data,
    customerEditOptions,
    partEditOptions,
    processEditOptions
  ]);

  const attributeDataTypeMap = useMemo(() => {
    const map: Record<string, AttributeDataType> = {};
    filterableAttributes.forEach((attr) => {
      map[attr.field] = attr.dataType;
    });
    return map;
  }, [filterableAttributes]);

  // Memoized sortable attributes for SearchFilterBar
  const sortableAttributes = useMemo((): SortableAttribute[] => {
    const formattedAttributes = formatAttributes(
      dynamicJobAttribute?.data?.data
    );
    if (!formattedAttributes || formattedAttributes.length === 0) return [];

    return formattedAttributes.map((attr: any) => ({
      id: String(attr.id),
      field: attr.name,
      label: attr.label || attr.name,
      nestedField: attr.nestedColumnName
    }));
  }, [dynamicJobAttribute?.data?.data]);

  return (
    <div className="aero:flex aero:flex-col aero:gap-4aero:w-full aero:max-w-full aero:px-4 aero:overflow-hidden">
      {/* Page Title */}
      <div className="aero:flex aero:items-center aero:justify-between aero:pb-2">
        <div className="aero:flex aero:items-center aero:gap-3">
          <div className="aero:w-10 aero:h-10 aero:rounded-lg aero:bg-brand-primary-50 aero:flex aero:items-center aero:justify-center">
            <ClipboardList className="aero:text-brand-primary-500 aero:h-[27px] aero:w-[27px] aero:p-0.5" />
          </div>
          <h1 className="aero:text-xl aero:md:text-2xl aero:text-foreground aero:font-bold aero:leading-9">
            Job Orders
          </h1>
        </div>
        <WorkspaceToggle />
      </div>

      {/* Search Filter Bar */}
      <SearchFilterBar
        placeholder="Search job orders..."
        searchValue={search}
        onSearchChange={setSearch}
        filterableAttributes={filterableAttributes}
        dynamicFilters={filters}
        onAddFilter={addFilter}
        onUpdateFilter={updateFilter}
        onRemoveFilter={removeFilter}
        onClearFilters={clearFilters}
        onForeignKeySearch={handleForeignKeySearch}
        sortableAttributes={sortableAttributes}
        dynamicSorts={sorts}
        onAddSort={addSort}
        onUpdateSort={updateSort}
        onRemoveSort={removeSort}
        onClearSorts={clearSorts}
      />

      {/* Main Card */}
      <Card className="aero:!rounded-lg aero:!border-neutral-300 aero:!p-4 aero:!gap-2 aero:!shadow-none">
        {/* Header with Add Job Order and Add Attribute buttons */}
        <div className="aero:flex aero:items-center aero:justify-end aero:gap-2 aero:pb-2">
          {/* WOR-1793 Req #9: Hide AddJobOrderDialog and AddAttributeDialog if no Plant3_ManageJob */}
          {permissions.canManagePlant3() && (
            <>
              <AddJobOrderSheet
                open={showJobOrderSheet}
                onOpenChange={setShowJobOrderSheet}
                attributes={
                  formatAttributes(dynamicJobAttribute?.data?.data).filter(
                    (f: any) => f.name !== "process"
                  ) || []
                }
                onSubmit={handleAddJobOrderDynamic}
                isLoading={
                  addJobOrderDynamic.isPending || addJobOrderWorkflow.isPending
                }
              />
              <Button
                type="button"
                intent="primary"
                onClick={() => setShowJobOrderSheet(true)}
              >
                <Plus className="aero:h-5 aero:w-5" />
                <span className="aero:font-bold aero:text-sm">
                  Add Job Order
                </span>
              </Button>
              <AddAttributeSheet
                onSubmit={handleSubmitNewAttribute}
                onOpenChange={setShowAttributeDialog}
                open={showAttributeDialog}
                dataTypes={jobOrderDynamicDataType?.data?.data}
              />
              <Button
                type="button"
                intent="primary"
                design="outline"
                onClick={() => setShowAttributeDialog(true)}
              >
                <Plus className="aero:h-5 aero:w-5" />
                <span className="aero:font-bold aero:text-sm">
                  Add Attribute
                </span>
              </Button>
            </>
          )}
        </div>

        {/* Divider */}
        <div className="aero:w-full aero:h-0 aero:border-t aero:border-neutral-300" />

        {/* Content */}
        <CardContent className="aero:!p-0 aero:!pt-4 aero:min-w-0 aero:overflow-x-auto">
          <DataTable<any, any>
            identifierKey="id"
            columns={columns || []}
            filterParams={{
              ...filterParams,

              ...(debouncedSearch && { search: debouncedSearch }),
              ...(debouncedFilters.length > 0 && {
                filters: serializeFiltersToLegacyFormat(
                  debouncedFilters,
                  attributeDataTypeMap
                )
              }),
              ...(debouncedSorts.length > 0 && {
                sorts: serializeSortsToLegacyFormat(debouncedSorts)
              })
            }}
            onRefetch={handleRefetch}
            onEditValue={handleEditValue}
            onForeignKeySearch={handleForeignKeySearch}
            onRowOrderChange={handleRowReorder}
            onRowDoubleClick={(row) => {
              const rowId = Number((row as any)?.id);
              if (Number.isNaN(rowId)) {
                return;
              }

              openJobOrderDetail(rowId, {
                initialTab: "process",
                workflowMode: permissions.canManagePlant3() ? "edit" : "view"
              });
            }}
            onTableReady={setTableInstance}
            api={JobOrdersDynamic}
            onActionClick={(id) => {
              const rowId = Number(id);
              if (Number.isNaN(rowId)) {
                return;
              }

              openJobOrderDetail(rowId, {
                initialTab: "job-information",
                workflowMode: "view"
              });
            }}
            headerActions={(columnId) => {
              return (
                permissions.canManagePlant3() && (
                  <ColumnHeaderActions
                    columnId={columnId}
                    form={form}
                    showEditDialog={showEditDialog}
                    showDeleteDialog={showDeleteDialog}
                    setSelectedAttribute={setSelectedAttribute}
                    setShowEditDialog={setShowEditDialog}
                    setShowDeleteDialog={setShowDeleteDialog}
                    systemAttributeIds={systemAttributeIds}
                  />
                )
              );
            }}
            features={{
              enableRowDragAndDrop: permissions.canManagePlant3(),
              enableSelection: permissions.canManagePlant3(),
              //  permissions.canManagePlant3()
              enableEditing: true
            }}
            actionItems={[
              {
                title: "Detail",
                icon: <ClipboardList className="aero:h-5 aero:w-5" />,
                className: "aero:bg-brand-primary-700",
                hidden: !permissions.canViewPlant3(),
                onClick: (row) => {
                  const rowId = Number(row.id);
                  if (Number.isNaN(rowId)) {
                    return;
                  }

                  openJobOrderDetail(rowId, {
                    initialTab: "job-information",
                    workflowMode: "view"
                  });
                }
              },
              {
                title: "Complete",
                icon: <CheckCheck className="aero:h-5 aero:w-5" />,
                className: "aero:bg-green-600",
                hidden: !permissions.canManagePlant3(),
                onClick: (row) => {
                  handleCompleteJob(row.id, true);
                }
              }
            ]}
          />
        </CardContent>
      </Card>

      {selectedItem.id ? (
        <DynamicSheetContent
          open={selectedItem.show}
          onOpenChange={(open) =>
            setSelectedItem((prev) => ({
              ...prev,
              show: open
            }))
          }
          id={selectedItem.id}
          refetch={refetch}
          canManage={permissions.canManagePlant3()}
          initialTab={selectedItem.initialTab}
          workflowMode={selectedItem.workflowMode}
        />
      ) : null}

      {/* HEADER TABLE SHEETS */}
      <EditHeaderActionSheet
        onSubmit={handleUpdateAttribute}
        setSelectedAttribute={setSelectedAttribute}
        dataTypes={jobOrderDynamicDataType?.data?.data}
        open={showEditDialog}
        setOpen={setShowEditDialog}
        form={form}
      />

      <DeleteHeaderActionDialog
        onSubmit={onDeleteAttribute}
        open={showDeleteDialog}
        selectedAttribute={selectedAttribute}
        setOpen={setShowDeleteDialog}
        setSelectedAttribute={setSelectedAttribute}
      />
    </div>
  );
}
